/**
 * Sound constraint calculation and validation utilities
 * Moved from types/animated.tsx for better organization and enhanced functionality
 */

import { 
    AnimatedItem, 
    SoundConfig, 
    VisibilityWindow, 
    SoundConstraints,
    calculateItemTotalVisibility,
    calculateSoundConstraints 
} from '../types/animated';
import { 
    SoundValidationResult, 
    TimingValidationConfig,
} from './types';

/**
 * Default timing validation configuration
 */
export const DEFAULT_TIMING_CONFIG: TimingValidationConfig = {
    allowTrimming: true,
    requireFadeForTrimming: true,
    minSoundDuration: 0.1,              // 100ms minimum
    maxDelayRatio: 0.8,                 // Delay can't exceed 80% of animation duration
    warningThresholds: {
        trimmingRatio: 0.5,             // Warn when trimming more than 50%
        volumeBelow: 0.1,               // Warn when volume below 10%
        delayRatio: 0.6                 // Warn when delay exceeds 60% of available time
    }
};

/**
 * Calculate enhanced timing constraints with validation
 */
export function calculateEnhancedSoundConstraints(
    item: AnimatedItem,
    soundConfig: SoundConfig ,
    soundType: 'entrance' | 'exit',
    validationConfig: TimingValidationConfig = DEFAULT_TIMING_CONFIG
): SoundConstraints {
    const baseConstraints = calculateSoundConstraints(item, soundConfig, soundType);
    
    // Add enhanced validation logic
    const originalDuration = soundConfig.metadata?.originalDuration ?? 0;
    const willBeTrimmed = originalDuration > baseConstraints.maxDuration;
    const trimmingRatio = willBeTrimmed ? 1 - (baseConstraints.maxDuration / originalDuration) : 0;
    
    // Calculate effective duration considering fade in/out
    const fadeIn = soundConfig.timing?.fadeIn ?? 0;
    const fadeOut = soundConfig.timing?.fadeOut ?? 0;
    const effectiveDuration = Math.max(0, baseConstraints.maxDuration - fadeIn - fadeOut);
    
    return {
        ...baseConstraints,
        willBeTrimmed,
        isValid: baseConstraints.isValid && effectiveDuration >= validationConfig.minSoundDuration
    };
}

/**
 * Comprehensive sound configuration validation
 */
export function validateSoundConfiguration(
    item: AnimatedItem,
    soundConfig: SoundConfig,
    soundType: 'entrance' | 'exit',
    validationConfig: TimingValidationConfig = DEFAULT_TIMING_CONFIG
): SoundValidationResult {
    const constraints = calculateEnhancedSoundConstraints(item, soundConfig, soundType, validationConfig);
    const warnings: string[] = [];
    const errors: string[] = [];
    const suggestions: string[] = [];
    
    const delay = soundConfig.timing?.delay ?? 0;
    const fadeIn = soundConfig.timing?.fadeIn ?? 0;
    const fadeOut = soundConfig.timing?.fadeOut ?? 0;
    const volume = soundConfig.timing?.volume ?? 1;
    const originalDuration = soundConfig.metadata?.originalDuration ?? 0;
    
    // Critical errors that prevent playback
    if (delay >= constraints.maxDuration) {
        errors.push(`Delay (${delay.toFixed(2)}s) exceeds available window (${constraints.maxDuration.toFixed(2)}s)`);
    }
    
    if (fadeIn + fadeOut > constraints.maxDuration) {
        errors.push(`Fade durations (${(fadeIn + fadeOut).toFixed(2)}s) exceed available window (${constraints.maxDuration.toFixed(2)}s)`);
    }
    
    if (constraints.maxDuration < validationConfig.minSoundDuration) {
        errors.push(`Available duration (${constraints.maxDuration.toFixed(2)}s) is below minimum (${validationConfig.minSoundDuration}s)`);
    }
    
    // Warnings for suboptimal configurations
    if (constraints.willBeTrimmed) {
        const trimmingRatio = 1 - (constraints.maxDuration / originalDuration);
        if (trimmingRatio > validationConfig.warningThresholds.trimmingRatio) {
            warnings.push(`Sound will be heavily trimmed (${(trimmingRatio * 100).toFixed(0)}% cut)`);
            
            if (validationConfig.requireFadeForTrimming && !fadeOut) {
                suggestions.push('Consider adding fade out to smooth trimmed ending');
            }
        } else {
            warnings.push(`Sound will be trimmed to ${constraints.maxDuration.toFixed(2)}s`);
        }
    }
    
    if (volume < validationConfig.warningThresholds.volumeBelow) {
        warnings.push(`Volume is very low (${(volume * 100).toFixed(0)}%)`);
        suggestions.push('Consider increasing volume for better audibility');
    }
    
    const delayRatio = delay / constraints.maxDuration;
    if (delayRatio > validationConfig.warningThresholds.delayRatio) {
        warnings.push(`Delay (${delay.toFixed(2)}s) uses ${(delayRatio * 100).toFixed(0)}% of available time`);
        suggestions.push('Consider reducing delay for longer sound playback');
    }
    
    // Optimization suggestions
    if (originalDuration > 0 && originalDuration < constraints.maxDuration * 0.5) {
        suggestions.push('Sound file is much shorter than available time - consider a longer sound');
    }
    
    if (!fadeIn && !fadeOut && constraints.willBeTrimmed) {
        suggestions.push('Add fade in/out for smoother audio transitions');
    }
    
    const effectivePlaybackTime = constraints.maxDuration - delay - fadeIn - fadeOut;
    if (effectivePlaybackTime < originalDuration * 0.3) {
        suggestions.push('Very little of the original sound will be audible - consider adjusting timing');
    }
    
    return {
        isValid: errors.length === 0 && constraints.isValid,
        warnings,
        errors,
        constraints,
        suggestions
    };
}

/**
 * Calculate optimal trimStart value for a sound within constraints
 */
export function calculateOptimalTrimStart(
    item: AnimatedItem,
    soundConfig: SoundConfig,
    soundType: 'entrance' | 'exit'
): number {
    const constraints = calculateSoundConstraints(item, soundConfig, soundType);
    const originalDuration = soundConfig.metadata?.originalDuration ?? 0;
    
    if (!constraints.willBeTrimmed || originalDuration === 0) {
        return soundConfig.timing?.trimStart ?? 0;
    }
    
    // If sound needs trimming, try to use the most interesting part
    // For entrance sounds, use the beginning
    // For exit sounds, use the end
    if (soundType === 'entrance') {
        return 0; // Start from beginning for entrance
    } else {
        // For exit sounds, calculate how much to trim from the start
        const excessDuration = originalDuration - constraints.maxDuration;
        return Math.max(0, excessDuration);
    }
}

/**
 * Calculate volume with fade processing for a given time
 */
export function calculateVolumeWithFades(
    currentTime: number,
    constraints: SoundConstraints,
    soundConfig: SoundConfig
): number {
    let volume = soundConfig.timing?.volume ?? 1;
    const relativeTime = currentTime - constraints.effectiveStart;
    const totalDuration = constraints.maxDuration;
    
    // Ensure we're within the playback window
    if (relativeTime < 0 || relativeTime > totalDuration) {
        return 0;
    }
    
    // Apply fade in
    const fadeIn = soundConfig.timing?.fadeIn ?? 0;
    if (fadeIn > 0 && relativeTime < fadeIn) {
        const fadeInProgress = relativeTime / fadeIn;
        volume *= Math.max(0, Math.min(1, fadeInProgress));
    }
    
    // Apply fade out
    const fadeOut = soundConfig.timing?.fadeOut ?? 0;
    if (fadeOut > 0) {
        const fadeOutStart = totalDuration - fadeOut;
        if (relativeTime > fadeOutStart) {
            const fadeOutProgress = (relativeTime - fadeOutStart) / fadeOut;
            volume *= Math.max(0, Math.min(1, 1 - fadeOutProgress));
        }
    }
    
    return Math.max(0, Math.min(1, volume));
}

/**
 * Check if sound should be playing at current time
 */
export function shouldSoundBePlaying(
    currentTime: number,
    constraints: SoundConstraints
): boolean {
    return currentTime >= constraints.effectiveStart && 
           currentTime < constraints.effectiveEnd;
}

/**
 * Calculate sound playback position in the original file
 */
export function calculateSoundFilePosition(
    currentTime: number,
    constraints: SoundConstraints,
    soundConfig: SoundConfig
): number {
    const relativeTime = currentTime - constraints.effectiveStart;
    const trimStart = soundConfig.timing?.trimStart ?? 0;
    
    return trimStart + relativeTime;
}

/**
 * Generate timing summary for debugging and user feedback
 */
export function generateTimingSummary(
    item: AnimatedItem,
    soundConfig: SoundConfig,
    soundType: 'entrance' | 'exit'
): string {
    const visibility = calculateItemTotalVisibility(item);
    const constraints = calculateSoundConstraints(item, soundConfig , soundType);
    const validation = validateSoundConfiguration(item, soundConfig, soundType);
    
    const lines = [
        `=== Sound Timing Summary ===`,
        `Item: ${item.id} (${soundType} sound)`,
        `Scheduled: ${item.startAt}s - ${item.startAt + (item.duration ?? 0)}s`,
        `Total Visibility: ${visibility.actualStart}s - ${visibility.actualEnd}s (${visibility.totalDuration}s)`,
        `Sound Window: ${constraints.windowStart}s - ${constraints.windowEnd}s`,
        `Effective Playback: ${constraints.effectiveStart}s - ${constraints.effectiveEnd}s`,
        `Max Duration: ${constraints.maxDuration.toFixed(2)}s`,
        `Will Be Trimmed: ${constraints.willBeTrimmed ? 'Yes' : 'No'}`,
        `Valid: ${validation.isValid ? 'Yes' : 'No'}`
    ];
    
    if (validation.errors.length > 0) {
        lines.push(`Errors: ${validation.errors.join(', ')}`);
    }
    
    if (validation.warnings.length > 0) {
        lines.push(`Warnings: ${validation.warnings.join(', ')}`);
    }
    
    return lines.join('\n');
}

/**
 * Re-export core functions from animated.tsx for convenience
 */
export {
    calculateItemTotalVisibility,
    calculateSoundConstraints
} from '../types/animated';